import { Divider, IconButton, ListItem, Stack, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { DeleteOutlined, QuestionAnswerOutlined } from "@mui/icons-material";
import React, { useState } from "react";
import { deleteResource, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { useSnackbar } from "notistack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import FormDraft from "../../../types/FormDraft";
import { UseFormReturn } from "react-hook-form";
import { CasePdfGeneratorFormValues } from "../../../types/CasePdfGenerator";
import { HashLink } from "react-router-hash-link";
import { Editor, useCurrentEditor } from "@tiptap/react";
import ApiClient from "../../../services/ApiClient";
import { getCasePdfFormParams, getDocumentName } from "./casePdfGeneratorService";
import StarterKit from "@tiptap/starter-kit";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { MediaObject } from "../../../types/MediaObject";
import { ProductUnion } from "../../../types/ProductUnion";
import dayjs from "dayjs";
import PdfGeneratorDraftFeedbackModal from "./PdfGeneratorDraftFeedbackModal";
import LegalbirdIoModal from "../../Modal/LegalbirdIoModal";
import ButtonLoading from "../../Button/ButtonLoading";
import { getDocumentRecipientRole } from "../../../services/documentRecipientService";

type PdfGeneratorDraftListItemProps = {
  product: ProductUnion;
  formDraft: FormDraft<CasePdfGeneratorFormValues>;
  formContext: UseFormReturn<CasePdfGeneratorFormValues>;
  mediaObjects: MediaObject[];
};
const PdfGeneratorDraftListItem = ({
  product,
  formDraft,
  formContext,
  mediaObjects,
}: PdfGeneratorDraftListItemProps) => {
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: deleteResource,
  });
  const { editor } = useCurrentEditor();
  const currentUser = useCurrentUser();
  const [confirmFeedbackSendModalOpen, setConfirmFeedbackSendModalOpen] = useState(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [inquiryText, setInquiryText] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const loadDraft = () => {
    const { content, selectedMediaObjects, ...restOfFormData } = formDraft.formData;
    //remove deleted/non-existent mediaObjects from the selectedMediaObjects list
    //filter to only have the valid mediaObjects and
    //map to get the original structure of the selectedMediaObjects array
    const validSelectedMediaObjectIds = mediaObjects
      .filter((mediaObject: MediaObject) => selectedMediaObjects.includes(mediaObject.id))
      .map((mediaObject: MediaObject) => mediaObject.id);

    formContext.reset({ ...restOfFormData, selectedMediaObjects: validSelectedMediaObjectIds });
    formContext.setValue("filename", getDocumentName(
      restOfFormData.recipientType,
      restOfFormData.filename ?? formDraft.title.replace(/^Entwurf_/, ""),
      restOfFormData.recipientType,
      getDocumentRecipientRole(product, restOfFormData.recipientType),
    ));
    editor?.commands.setContent(content);
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ uri: "form_drafts", id: formDraft.id });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.collection("form_drafts"),
      });
      setConfirmDeleteModalOpen(false);
    } catch (error) {
      enqueueSnackbar("Es ist ein Fehler aufgetreten", {
        variant: "error",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }
  };

  const handleSendFeedback = async (): Promise<boolean> => {
    const { content, ...restOfFormData } = formDraft.formData;
    if (restOfFormData.documentTemplate !== "extrajudicialLetter" && !restOfFormData.recipientType) {
      enqueueSnackbar("Bitte im Entwurf einen Empfänger auswählen und speichern.", { variant: "error" });
      return false;
    }
    const loadedEditorContent = new Editor({ content: content, extensions: [StarterKit] });

    let response;

    try {
      response = await ApiClient.post("lb/services/generate_case_file", {
        body: JSON.stringify({
          createdBy: currentUser["@id"],
          productClassName: product.productClassName,
          productId: product.id,
          documentIdentifier: "customPdf",
          showInGallery: false,
          showInExternalView: false,
          params: {
            ...getCasePdfFormParams({ values: restOfFormData, editor: loadedEditorContent, currentUser }),
            filename: getDocumentName(restOfFormData.recipientType, restOfFormData.filename ?? formDraft.title.replace(/^Entwurf_/, "")),
            fieldName: "customPdfDraft",
            electronicFileFolderPath: null,
          },
        }),
      });
    } catch (error) {
      enqueueSnackbar("Beim Erstellen des Entwurfs ist ein Fehler aufgetreten.", {
        variant: "error",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });

      return false;
    }

    try {
      await ApiClient.post("/lb/services/generate_inquiry_to_customer", {
        body: JSON.stringify({
          productId: product.id,
          inquiryType: "customPdfDraft",
          createdBy: currentUser["@id"],
          inquiryTextInformation: inquiryText,
          attachedMediaObjects: [response["mediaObjectId"]],
        }),
      });
    } catch (error) {
      enqueueSnackbar("Der Entwurf wurde erstellt, aber die Anfrage konnte nicht versendet werden.", {
        variant: "error",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });

      return false;
    }

    enqueueSnackbar("Der Entwurf wurde erstellt und die Anfrage wurde versendet.", {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });

    return true;
  };

  return (
    <>
      <ListItem key={formDraft.id}>
        <Stack
          direction={"row"}
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography>{formDraft.title + " | " + formDraft.creator.person.fullname}</Typography>
          <Stack
            direction={"row"}
            spacing={1}
            sx={{
              alignItems: "center",
            }}
          >
            <Typography sx={{ paddingRight: "10px" }}>{dayjs(formDraft.created).format("HH:mm")} Uhr</Typography>
            <Tooltip title="Entwurf bearbeiten">
              <IconButton component={HashLink} onClick={loadDraft} to={"#lbEditorAnchor"}>
                <EditIcon color={"primary"} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Feedback einholen">
              <IconButton onClick={() => setConfirmFeedbackSendModalOpen(true)}>
                <QuestionAnswerOutlined color={"primary"} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Entwurf löschen">
              <IconButton onClick={() => setConfirmDeleteModalOpen(true)}>
                <DeleteOutlined color={"primary"} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </ListItem>
      <Divider />
      <PdfGeneratorDraftFeedbackModal
        open={confirmFeedbackSendModalOpen}
        formContext={formContext}
        onClose={() => setConfirmFeedbackSendModalOpen(false)}
        onSubmit={handleSendFeedback}
        onInquiryTextChange={setInquiryText}
      />
      <LegalbirdIoModal
        open={confirmDeleteModalOpen}
        handleClose={() => setConfirmDeleteModalOpen(false)}
        title={"Entwurf löschen?"}
        submitButton={
          <ButtonLoading variant={"contained"} isLoading={deleteMutation.isPending} onClick={handleDelete}>
            Entwurf löschen
          </ButtonLoading>
        }
      >
        <Typography>
          Möchten Sie diesen Entwurf wirklich löschen? <br />
          <br />
          <strong>{formDraft.title}</strong>
        </Typography>
      </LegalbirdIoModal>
    </>
  );
};

export default PdfGeneratorDraftListItem;
